import { useCallback, useMemo, type ReactNode } from "react";

import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";

import { toGridPagination } from "app/components/table/StackletGrid";
import { useCursorPagination } from "app/hooks/grid";

import { GroupedUsersGrid } from "./GroupedUsersGrid";
import { groupingDetailPath, type RetainedSearch } from "./userGrouping";

import type { GroupUsersGrouping_query$key } from "./__generated__/GroupUsersGrouping_query.graphql";
import type { GroupUsersGroupingQuery } from "./__generated__/GroupUsersGroupingQuery.graphql";
import type { GroupUsersGroupingRefetchQuery } from "./__generated__/GroupUsersGroupingRefetchQuery.graphql";
import type { BucketRow } from "./GroupedUsersGrid";
import type { CursorVariables } from "app/hooks/grid";

/** Groups shown per page in the bucket list. */
const GROUP_PAGE = 25;
/** Selectable page sizes for the bucket pager. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/** Lists user groups a page at a time; expanding one shows its members. */
export function GroupUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const queryData = useLazyLoadQuery<GroupUsersGroupingQuery>(
    graphql`
      query GroupUsersGroupingQuery($first: Int) {
        ...GroupUsersGrouping_query @arguments(first: $first)
      }
    `,
    { first: GROUP_PAGE },
  );

  const [data, refetch] = useRefetchableFragment<
    GroupUsersGroupingRefetchQuery,
    GroupUsersGrouping_query$key
  >(
    graphql`
      fragment GroupUsersGrouping_query on Query
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
      )
      @refetchable(queryName: "GroupUsersGroupingRefetchQuery") {
        userGroups(first: $first, last: $last, after: $after, before: $before) {
          edges {
            node {
              id
              uuid
              name
              displayName
              source {
                idpAlias
                idpNamespace
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows = useMemo<BucketRow[]>(
    () =>
      (data.userGroups.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => {
          const label = node.displayName || node.name;
          return {
            id: node.id,
            label,
            to: groupingDetailPath({
              kind: "group",
              uuid: node.uuid,
              name: label,
            }),
            editTo: `/settings/access-control/groups/${node.uuid}/edit`,
            memberScope: { kind: "group-uuid" as const, groupUuid: node.uuid },
            highlightGroupUuid: node.uuid,
            detail: node.source
              ? `Synced from ${node.source.idpAlias || node.source.idpNamespace}`
              : "Local",
          };
        }),
    [data.userGroups.edges],
  );

  const onNavigate = useCallback(
    (variables: CursorVariables) =>
      refetch(variables, { fetchPolicy: "network-only" }),
    [refetch],
  );

  const cursor = useCursorPagination({
    pageInfo: data.userGroups.pageInfo,
    rowCount: rows.length,
    defaultPageSize: GROUP_PAGE,
    onNavigate,
  });

  const { hasNextPage, hasPreviousPage, total } = data.userGroups.pageInfo;
  const pagination = toGridPagination(
    cursor,
    { hasNextPage, hasPreviousPage, total },
    { unit: "group", pageSizeOptions: PAGE_SIZE_OPTIONS },
  );

  return (
    <GroupedUsersGrid
      buckets={rows}
      initialSearchTerm={initialSearchTerm}
      loading={cursor.isPending}
      onSearchTermChange={onSearchTermChange}
      pagination={pagination}
      searchKind="group"
      tableId="grouped-users-groups"
      toolbarActions={toolbarActions}
      unit="group"
    />
  );
}
